import { Image, Platform, StyleSheet, View } from 'react-native';
import type { AgentModelBrand } from '@tabbio/utils/agent-models';
import { Text } from '@/components/common/AppText';
import { useTheme } from '@/hooks/useTheme';
import { useFont } from '@/providers/FontProvider';

const GEMINI_WORDMARK = require('../../../assets/onboarding/logo-gemini.png') as number;
const CLAUDE_WORDMARK = require('../../../assets/onboarding/logo-sonnet.png') as number;

const WORDMARKS: Partial<
  Record<
    AgentModelBrand,
    { source: number; aspectRatio: number; cropScale?: number }
  >
> = {
  // The source files are legacy wordmarks. The fixed square viewport clips
  // them to the provider mark, while the current model name comes from the
  // shared catalog instead of being baked into an image.
  gemini: { source: GEMINI_WORDMARK, aspectRatio: 288 / 54 },
  // Claude's legacy asset begins with the orange star followed immediately by
  // a baked-in "C". Slightly enlarging it inside the square crop keeps just
  // the star visible, instead of producing a duplicate C beside the live name.
  claude: { source: CLAUDE_WORDMARK, aspectRatio: 354 / 54, cropScale: 1.35 },
};

export function AgentModelMark({
  brand,
  size = 18,
}: {
  brand: AgentModelBrand;
  size?: number;
}) {
  const wordmark = WORDMARKS[brand];
  if (!wordmark) return null;
  const cropScale = wordmark.cropScale ?? 1;

  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[styles.viewport, { width: size, height: size }]}
    >
      <Image
        source={wordmark.source}
        resizeMode="contain"
        style={{
          width: size * wordmark.aspectRatio * cropScale,
          height: size * cropScale,
          transform: [{ translateY: (-size * (cropScale - 1)) / 2 }],
        }}
      />
    </View>
  );
}

/**
 * Current model identity rendered as one wordmark unit.
 *
 * The bundled provider images still contain legacy model names, so the picker
 * cannot safely unclip them. This keeps their authentic mark and pairs it with
 * the canonical name from the shared model catalog, preventing the visual
 * identity from drifting when a runtime model changes.
 */
export function AgentModelWordmark({
  model,
  size = 18,
  maxWidth,
  color,
}: {
  model: { brand: AgentModelBrand; displayName: string };
  size?: number;
  maxWidth?: number;
  color?: string;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const isClaude = model.brand === 'claude';
  const fontFamily = Platform.select({
    web: isClaude
      ? 'Arial, Helvetica, sans-serif'
      : "'Google Sans', Roboto, Arial, sans-serif",
    default: isClaude ? fonts.regular : fonts.medium,
  });

  return (
    <View
      accessible
      accessibilityLabel={model.displayName}
      style={[styles.wordmark, maxWidth ? { maxWidth } : null]}
    >
      <AgentModelMark brand={model.brand} size={size} />
      <Text
        numberOfLines={1}
        style={{
          flexShrink: 1,
          color: color ?? theme.text,
          fontFamily,
          fontSize: Math.max(12, Math.round(size * 0.82)),
          fontWeight: isClaude ? '400' : '500',
          lineHeight: Math.round(size * 1.15),
        }}
      >
        {model.displayName}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  viewport: {
    flexShrink: 0,
    overflow: 'hidden',
  },
  wordmark: {
    minWidth: 0,
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
});
